import 'react-app-polyfill/ie9';
import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import getMuiTheme from 'material-ui/styles/getMuiTheme';
import MuiThemeProvider from 'material-ui/styles/MuiThemeProvider';
import createStore from './store';
import { setDataProvider } from './data-providers';
import nativeBridge, { initNativeBridge } from './native';
import DevWrapper from './development/dev-wrapper';
import App from './app';
import onboardingTheme from './theme/onboarding-theme';
import getInitialPage from './helpers/get-initial-page';
import getPaymentTypeWolf from './helpers/get-payment-type-wolf';

import PhoenixPageNavigator from './navigation/phoenix';
import PhoenixDataProvider from './data-providers/phoenix';

import WolfPageNavigator, { pages as wolfPages } from './navigation/wolf';
import WolfDataProvider from './data-providers/wolf';

import { getUserProfile } from './store/ducks/user';

import './sass/index.css';

const segmentKeys = {
  wolf: {
    development: 'wuPQTm1XtyQplR3eFPN0Huq8RXBuGNZs',
    production: 'BoxlmDdOfn317m3cqX5u3KTxfkiM1oKB'
  }
};

if (DevWrapper.shouldRenderShell() && process.env.REACT_APP_DISABLE_DEV_WRAPPER !== 'true') {
  ReactDOM.render(<DevWrapper />, document.getElementById('root'));
} else {
  initNativeBridge();
  getConfigValues().then(startApp);
}

function getConfigValues() {
  if (process.env.NODE_ENV === 'development') {
    return Promise.resolve({
      appName: process.env.REACT_APP_APP_NAME || 'wolf', // set to 'wolf' to test wolf
      baseUrl: process.env.REACT_APP_CLIENT_BASE_URL,
      bearerToken: process.env.REACT_APP_BEARER_TOKEN,
      clientId: process.env.REACT_APP_CLIENT_ID,
      clientSecret: process.env.REACT_APP_CLIENT_SECRET,
      currencyCode: 'USD',
      hasFreeTrial: true,
      mediaType: 'tablet', // set mediaType here 'tablet' || 'phone'
      signInWithAppleEnabled: process.env.REACT_APP_APPLE_DEVICE === 'true'
      // planetFitnessSSOCode: '123' // uncomment to mock PF deep link
    });
  }

  return nativeBridge.getConfigValuesFromNative();
}

async function startApp({
  appName,
  baseUrl,
  bearerToken,
  clientId,
  clientSecret,
  currencyCode,
  hasFreeTrial,
  mediaType,
  signInWithAppleEnabled,
  moesifAppId,
  planetFitnessSSOCode,
  swNumber,
}) {
  let navigatorMiddleware;
  let dataProvider;
  let initialPage;
  let pages;
  let initialState = {};

  switch (getApp(appName)) {
    case 'phoenix':
      loadSegment('phoenix');
      initialPage = getInitialPage(PhoenixPageNavigator.getPageList());
      navigatorMiddleware = new PhoenixPageNavigator(initialPage);
      pages = PhoenixPageNavigator.getPages();
      dataProvider = new PhoenixDataProvider({ clientId, clientSecret, baseUrl, bearerToken, moesifAppId });
      initialState = { config: { appName, mediaType } };
      break;
    case 'wolf':
      loadSegment('wolf');
      initialPage = planetFitnessSSOCode
        ? wolfPages.LOGIN_WITH_EMAIL
        : getInitialPage(WolfPageNavigator.getPageList());
      navigatorMiddleware = new WolfPageNavigator(initialPage);
      pages = WolfPageNavigator.getPages();
      dataProvider = new WolfDataProvider({ clientId, clientSecret, baseUrl, bearerToken, moesifAppId });
      const paymentType = getPaymentTypeWolf(swNumber);
      initialState = { payment: { paymentType, hasFreeTrial, currencyCode }, config: { appName, mediaType, signInWithAppleEnabled } };
      break;
    default:
      throw new Error(`Application ${getApp()} is not supported.`);
  }

  const store = createStore(navigatorMiddleware, initialState);
  setDataProvider(dataProvider);

  // if native has provided a bearer token and initial page, let's go ahead and load the user profile
  if (bearerToken && initialPage) {
    try {
      await store.dispatch(getUserProfile());
    } catch (err) {
      /* do nothing */
    }
  }

  initNativeBridge(store);
  nativeBridge.listenForUpdates();

  ReactDOM.render(
    <Provider store={ store }>
      <MuiThemeProvider muiTheme={ getMuiTheme(onboardingTheme) }>
        <App pages={ pages } planetFitnessSSOCode={ planetFitnessSSOCode } />
      </MuiThemeProvider>
    </Provider>,
    document.getElementById('root')
  );
}

function loadSegment(appName) {
  const appEnvs = segmentKeys[appName];
  const appKey = appEnvs ? appEnvs[process.env.NODE_ENV] : undefined;
  if (!appKey) {
    window.analytics = null;
    return;
  }

  window.analytics.load(appKey);
  window.analytics.page();
}

function getApp(appName) {
  return appName === 'wolf' ? 'wolf' : 'phoenix';
}
